import React, { useState } from "react";
import { Button, Drawer, Input, Select, Skeleton, Table } from "antd";

import {
  Chip,
  DECISION_COLOR,
  MetricCard,
  SectionCard,
  fmtNum,
  fmtPct,
  Term,
  useMasteryApi,
} from "./shared";

// ── API payloads ──────────────────────────────────────────────────────────────
// Ported from the standalone Golden Explorer's /api/scores/* routes (see
// views_portfolio_mastery_scores.py). The histogram is always the full
// population; funnel/baskets/signals/taxonomy/judges recompute for the
// selected confidence band.

interface Cutoffs {
  accept_min: number | null;
  accept_max: number | null;
  review_min: number | null;
  review_max: number | null;
  observed: boolean;
  dead_band: [number | null, number | null];
}

interface BasketRow {
  basket: string;
  n: number;
  mean_conf: number | null;
  approved: number;
  review: number;
  rejected: number;
}

interface Funnel {
  scored: number;
  auto_accepted: number;
  human_review: number;
  auto_rejected: number;
  below_cutoff_merges: number;
}

interface DistributionPayload {
  hist: Record<string, number[]>;
  n_bins: number;
  banded: boolean;
  cutoffs: Cutoffs;
  baskets: BasketRow[];
  funnel: Funnel;
  review_outcomes: Array<{ status: string; n: number }>;
}

interface SignalRow {
  label: string;
  coverage_pct: number | null;
  mean_accepted: number | null;
  mean_rejected: number | null;
}

interface TaxonomyRow {
  relationship_type: string;
  status: string;
  n: number;
}

interface JudgeRow {
  actor_id: string;
  match_status: string;
  n: number;
}

interface PairRow {
  id: number;
  norm_site_id_a: string;
  name_a: string | null;
  city_a: string | null;
  norm_site_id_b: string;
  name_b: string | null;
  city_b: string | null;
  golden_a: string | null;
  golden_b: string | null;
  confidence: number | null;
  weighted_score: number | null;
  accept_basket: string | null;
  match_status: string;
  blocking_keys: string | null;
  signals_present: number | null;
  [signal: `score_${string}`]: number | null;
}

interface PairsPayload {
  results: PairRow[];
  count: number;
}

interface DrilldownPayload {
  site: {
    norm_site_id: string;
    norm_site_name: string | null;
    norm_city: string | null;
    golden_site_id: string | null;
  } | null;
  counterparts: Array<{
    other_id: string;
    other_name: string | null;
    other_city: string | null;
    other_golden: string | null;
    confidence: number | null;
    accept_basket: string | null;
    match_status: string;
  }>;
}

// ── constants ─────────────────────────────────────────────────────────────────

// The 15 scoring signals per pair row, in display order (mirrors the backend
// SIGNALS constant in views_portfolio_mastery_scores.py).
const SIGNAL_FIELDS: Array<[`score_${string}`, string]> = [
  ["score_site_name", "Site name"],
  ["score_institution", "Institution"],
  ["score_address", "Address"],
  ["score_geo", "Geo distance"],
  ["score_phone", "Phone"],
  ["score_email_domain", "Email domain"],
  ["score_nct", "NCT overlap"],
  ["score_site_continuity", "Site continuity"],
  ["score_pi_name", "PI name"],
  ["score_pi_rarity", "PI rarity"],
  ["score_inv_network", "Investigator network"],
  ["score_irb", "IRB"],
  ["score_site_number", "Site number"],
  ["score_coordinator", "Coordinator"],
  ["score_external_id", "External ID"],
];

const STATUS_META: Record<string, { label: string; color: string }> = {
  auto_accepted: { label: "auto-accepted", color: DECISION_COLOR.accept },
  human_review: { label: "human review", color: DECISION_COLOR.review },
  auto_rejected: { label: "auto-rejected", color: DECISION_COLOR.reject },
};

const PAGE_SIZE = 50;

// confidence value (0–1) → percent string
const fmtConf = (v: number | null | undefined, digits = 1) =>
  v == null ? "—" : `${(v * 100).toFixed(digits)}%`;
const dash = (v: string | number | null | undefined) =>
  v == null || v === "" ? "—" : String(v);

// ── presentational pieces ─────────────────────────────────────────────────────

function DecisionPill({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, color: "#556C75" };
  return (
    <span
      className="whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium"
      style={{ color: meta.color, background: `${meta.color}1A` }}
    >
      {meta.label}
    </span>
  );
}

/** Thin inline bar for table cells (signal means, basket volumes). */
function InlineBar({
  frac,
  color,
  width = 100,
}: {
  frac: number;
  color: string;
  width?: number;
}) {
  return (
    <div className="h-2.5 rounded bg-[#f1f5f9]" style={{ width }}>
      <div
        className="h-full rounded"
        style={{
          width: `${Math.min(Math.max(frac, 0), 1) * 100}%`,
          background: color,
        }}
      />
    </div>
  );
}

/** Stacked 40-bin confidence histogram colored by decision, built from plain
 * divs. Clicking a bin selects its confidence band; clicking again clears. */
function ConfidenceHistogram({
  hist,
  nBins,
  cutoffs,
  selectedBin,
  onBinClick,
}: {
  hist: Record<string, number[]>;
  nBins: number;
  cutoffs: Cutoffs;
  selectedBin: number | null;
  onBinClick: (bin: number) => void;
}) {
  const statuses = Object.keys(STATUS_META).filter((s) => hist[s]);
  const bins = Array.from({ length: nBins }, (_, bin) => ({
    bin,
    total: statuses.reduce((acc, s) => acc + (hist[s][bin] ?? 0), 0),
  }));
  const maxTotal = Math.max(...bins.map((b) => b.total), 1);
  const markers = [
    {
      at: cutoffs.accept_min,
      label: `auto-accept ≥ ${fmtConf(cutoffs.accept_min, 0)}`,
    },
    {
      at: cutoffs.review_max,
      label: `review ≤ ${fmtConf(cutoffs.review_max, 0)}`,
    },
  ].filter((m): m is { at: number; label: string } => m.at != null);

  return (
    <div>
      <div className="relative h-44">
        {markers.map((m) => (
          <div
            key={m.label}
            className="absolute bottom-0 top-0 border-l border-dashed border-[#556C75]"
            style={{ left: `${m.at * 100}%` }}
          >
            <span className="absolute -top-0.5 left-1 whitespace-nowrap text-[10.5px] text-[#556C75]">
              {m.label}
            </span>
          </div>
        ))}
        <div className="flex h-full items-end gap-px">
          {bins.map(({ bin, total }) => (
            <button
              key={bin}
              type="button"
              onClick={() => onBinClick(bin)}
              aria-label={`Confidence ${fmtConf(bin / nBins, 1)}–${fmtConf((bin + 1) / nBins, 1)}: ${fmtNum(total)} pairs`}
              className="flex h-full min-w-0 flex-1 cursor-pointer flex-col justify-end border-0 bg-transparent p-0"
              style={{
                opacity: selectedBin == null || selectedBin === bin ? 1 : 0.35,
                outline: selectedBin === bin ? "2px solid #2B4752" : "none",
                outlineOffset: 1,
              }}
              title={`${fmtConf(bin / nBins, 1)}–${fmtConf((bin + 1) / nBins, 1)} · ${fmtNum(total)} pairs`}
            >
              {/* stacked: rejected on top, accepted at the bottom */}
              {["auto_rejected", "human_review", "auto_accepted"]
                .filter((s) => hist[s])
                .map((s) => (
                  <div
                    key={s}
                    style={{
                      height: `${((hist[s][bin] ?? 0) / maxTotal) * 100}%`,
                      background: STATUS_META[s].color,
                    }}
                  />
                ))}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-1 flex justify-between text-xs tabular-nums text-[#556C75]">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-4">
        {Object.entries(STATUS_META).map(([s, meta]) => (
          <span
            key={s}
            className="inline-flex items-center gap-1.5 text-xs text-[#2B4752]"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: meta.color }}
            />
            {meta.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Full 15-signal breakdown for one pair row. */
function SignalBreakdown({ row }: { row: PairRow }) {
  return (
    <div className="max-w-2xl">
      {SIGNAL_FIELDS.map(([field, label]) => {
        const score = row[field];
        return (
          <div
            key={field}
            className="my-1 grid grid-cols-[150px_1fr_60px] items-center gap-2.5"
          >
            <span className="text-right text-xs text-[#556C75]">{label}</span>
            <div className="h-2.5 rounded bg-[#f1f5f9]">
              <div
                className="h-full rounded"
                style={{
                  width: `${(score ?? 0) * 100}%`,
                  background: "#0E7490",
                }}
              />
            </div>
            <span className="text-right text-xs tabular-nums text-[#2B4752]">
              {score == null ? "—" : score.toFixed(2)}
            </span>
          </div>
        );
      })}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[#2B4752]">
        <span>
          Blocking keys:{" "}
          <span className="text-xs">{dash(row.blocking_keys)}</span>
        </span>
        <span>
          Signals present:{" "}
          <span className="font-semibold tabular-nums">
            {fmtNum(row.signals_present)}
          </span>
        </span>
        <span>
          Golden A: <span className="text-xs">{dash(row.golden_a)}</span>
        </span>
        <span>
          Golden B: <span className="text-xs">{dash(row.golden_b)}</span>
        </span>
      </div>
    </div>
  );
}

/** Drawer body: every scored pair a normalized site participates in. */
function SiteDrilldown({ nsid }: { nsid: string }) {
  const { data, isLoading } = useMasteryApi<DrilldownPayload>(
    `mastery-scores/site/${encodeURIComponent(nsid)}/`,
  );
  if (isLoading || !data) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[#2B4752]">
        <span>
          Site:{" "}
          <span className="font-semibold">
            {dash(data.site?.norm_site_name)}
          </span>
        </span>
        <span>City: {dash(data.site?.norm_city)}</span>
        <span>
          Golden id:{" "}
          <span className="text-xs">{dash(data.site?.golden_site_id)}</span>
        </span>
      </div>
      <Table
        size="small"
        rowKey={(r) => `${r.other_id}-${r.confidence ?? ""}`}
        dataSource={data.counterparts}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        columns={[
          {
            title: "Counterpart",
            dataIndex: "other_name",
            ellipsis: true,
            render: (v: string | null) => dash(v),
          },
          {
            title: "City",
            dataIndex: "other_city",
            width: 130,
            render: (v: string | null) => dash(v),
          },
          {
            title: "Golden id",
            dataIndex: "other_golden",
            width: 160,
            render: (v: string | null) => (
              <span className="text-xs">{dash(v)}</span>
            ),
          },
          {
            title: "Confidence",
            dataIndex: "confidence",
            width: 100,
            render: (v: number | null) => (
              <span className="tabular-nums">{fmtConf(v)}</span>
            ),
          },
          {
            title: "Decision",
            dataIndex: "match_status",
            width: 130,
            render: (v: string) => <DecisionPill status={v} />,
          },
        ]}
      />
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function MasteryScoreExplorer() {
  const [selectedBin, setSelectedBin] = useState<number | null>(null);
  const [status, setStatus] = useState("");
  const [basket, setBasket] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [drill, setDrill] = useState<string | null>(null);

  const { data: dist } = useMasteryApi<DistributionPayload>(
    "mastery-scores/distribution/",
  );
  const nBins = dist?.n_bins ?? 40;
  const band =
    selectedBin == null
      ? {}
      : {
          min_conf: +(selectedBin / nBins).toFixed(4),
          max_conf: +((selectedBin + 1) / nBins).toFixed(4),
        };

  // same query as `dist` when no band is selected (identical key → cached)
  const { data: bandedDist } = useMasteryApi<DistributionPayload>(
    "mastery-scores/distribution/",
    band,
  );
  const { data: sig } = useMasteryApi<{ signals: SignalRow[] }>(
    "mastery-scores/signals/",
    band,
  );
  const { data: tax } = useMasteryApi<{ taxonomy: TaxonomyRow[] }>(
    "mastery-scores/taxonomy/",
    band,
  );
  const { data: judges } = useMasteryApi<{ judges: JudgeRow[] }>(
    "mastery-scores/judges/",
    band,
  );
  const { data: pairs, isFetching: pairsLoading } = useMasteryApi<PairsPayload>(
    "mastery-scores/pairs/",
    {
      status: status || undefined,
      basket: basket || undefined,
      q: q || undefined,
      ...band,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    },
  );

  if (!dist) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  const view = bandedDist ?? dist;
  const f = view.funnel;
  const cut = dist.cutoffs; // cutoffs always from the full population
  const maxBasket = Math.max(...view.baskets.map((b) => b.n), 1);

  const clickBin = (i: number) => {
    setPage(1);
    setSelectedBin((prev) => (prev === i ? null : i));
  };
  const clearBand = () => {
    setPage(1);
    setSelectedBin(null);
  };

  // taxonomy: relationship type → total reviews + merge rate
  const taxAgg: Record<string, { total: number; merged: number }> = {};
  (tax?.taxonomy ?? []).forEach((t) => {
    taxAgg[t.relationship_type] = taxAgg[t.relationship_type] ?? {
      total: 0,
      merged: 0,
    };
    taxAgg[t.relationship_type].total += t.n;
    if (t.status === "accepted_merge") {
      taxAgg[t.relationship_type].merged += t.n;
    }
  });

  // judges: actor → per-status decision counts
  const judgeAgg: Record<string, Record<string, number>> = {};
  (judges?.judges ?? []).forEach((j) => {
    judgeAgg[j.actor_id] = judgeAgg[j.actor_id] ?? {};
    judgeAgg[j.actor_id][j.match_status] = j.n;
  });

  return (
    <div>
      {/* funnel + observed cutoffs, recomputed for the selected band */}
      <div className="mb-4 grid grid-cols-4 gap-4">
        <MetricCard
          label={view.banded ? "Scored pairs (in band)" : "Scored pairs"}
          value={fmtNum(f.scored)}
          rows={[
            { label: "Auto-accepted", value: fmtNum(f.auto_accepted) },
            { label: "Human review", value: fmtNum(f.human_review) },
            { label: "Auto-rejected", value: fmtNum(f.auto_rejected) },
          ]}
        />
        <MetricCard
          label="Auto-accept cutoff (observed)"
          value={`≥ ${fmtConf(cut.accept_min)}`}
          rows={[
            { label: "Highest accepted", value: fmtConf(cut.accept_max) },
            {
              label: "Dead band",
              value: `${fmtConf(cut.dead_band?.[0])}–${fmtConf(cut.dead_band?.[1])}`,
            },
          ]}
        />
        <MetricCard
          label="Review band (observed)"
          value={`${fmtConf(cut.review_min)}–${fmtConf(cut.review_max)}`}
          rows={[
            { label: "Sent to review", value: fmtNum(f.human_review) },
            {
              label: "Share of scored",
              value:
                f.scored > 0
                  ? `${((100 * f.human_review) / f.scored).toFixed(1)}%`
                  : "—",
            },
          ]}
        />
        <MetricCard
          label="Merged below cutoff"
          value={fmtNum(f.below_cutoff_merges)}
          rows={
            view.review_outcomes.length > 0
              ? view.review_outcomes.slice(0, 3).map((r) => ({
                  label: r.status.replace(/_/g, " "),
                  value: fmtNum(r.n),
                }))
              : [{ label: "Pair reviews", value: "0" }]
          }
        />
      </div>

      <SectionCard
        title="Score distribution × decision"
        meta="click a bar to filter the page to that confidence band · click again to clear"
        extra={
          selectedBin != null ? (
            <span className="text-sm text-[#2B4752]">
              Confidence {fmtConf(band.min_conf, 1)}–{fmtConf(band.max_conf, 1)}
              <Button size="small" className="ml-2" onClick={clearBand}>
                Clear selection
              </Button>
            </span>
          ) : undefined
        }
      >
        <ConfidenceHistogram
          hist={dist.hist}
          nBins={nBins}
          cutoffs={cut}
          selectedBin={selectedBin}
          onBinClick={clickBin}
        />
      </SectionCard>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Accept baskets"
          meta="pairs that matched an accept rule"
        >
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-[#556C75]">
                  <th className="p-1.5 text-left">Basket</th>
                  <th className="p-1.5 text-left">Pairs</th>
                  <th className="p-1.5 text-right">n</th>
                  <th className="p-1.5 text-right">Mean conf</th>
                  <th className="p-1.5 text-right">Accepted</th>
                  <th className="p-1.5 text-right">Review</th>
                  <th className="p-1.5 text-right">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {view.baskets.map((b) => (
                  <tr key={b.basket} className="border-t border-[#f0f0f0]">
                    <td className="p-1.5 text-[#2B4752]">
                      <Term term={b.basket} />
                    </td>
                    <td className="p-1.5">
                      <InlineBar frac={b.n / maxBasket} color="#0E7490" />
                    </td>
                    <td className="p-1.5 text-right tabular-nums">
                      {fmtNum(b.n)}
                    </td>
                    <td className="p-1.5 text-right tabular-nums">
                      {fmtConf(b.mean_conf)}
                    </td>
                    <td className="p-1.5 text-right tabular-nums">
                      {fmtNum(b.approved)}
                    </td>
                    <td className="p-1.5 text-right tabular-nums">
                      {fmtNum(b.review)}
                    </td>
                    <td className="p-1.5 text-right tabular-nums">
                      {fmtNum(b.rejected)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {view.baskets.length === 0 && (
            <p className="text-sm text-[#2B4752]">
              No accept-basket pairs in this confidence band.
            </p>
          )}
        </SectionCard>

        <SectionCard
          title="Review outcomes (golden pair level)"
          glossaryTerm="review_outcomes"
        >
          {view.review_outcomes.map((r) => (
            <div
              key={r.status}
              className="flex items-center justify-between py-1"
            >
              <DecisionPill status={r.status} />
              <span className="text-sm font-semibold tabular-nums text-[#2B4752]">
                {fmtNum(r.n)}
              </span>
            </div>
          ))}
          {view.review_outcomes.length === 0 && (
            <p className="text-sm text-[#2B4752]">
              No golden-pair reviews in this confidence band.
            </p>
          )}
          <div className="mt-2 border-t border-[#E6EEF2] pt-2 text-sm text-[#2B4752]">
            Merged below the auto-accept cutoff:{" "}
            <span className="font-semibold tabular-nums">
              {fmtNum(f.below_cutoff_merges)}
            </span>
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="Signal importance"
        meta="what drives the score"
        glossaryTerm="signal_importance"
      >
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-[#556C75]">
                <th className="p-1.5 text-left">Signal</th>
                <th className="p-1.5 text-right">Coverage</th>
                <th className="p-1.5 text-right">Mean (accepted)</th>
                <th className="p-1.5 text-right">Mean (rejected)</th>
                <th className="p-1.5 text-left">Discrimination</th>
              </tr>
            </thead>
            <tbody>
              {(sig?.signals ?? []).map((s) => (
                <tr key={s.label} className="border-t border-[#f0f0f0]">
                  <td className="p-1.5 text-[#2B4752]">
                    <Term term={s.label} />
                  </td>
                  <td className="p-1.5 text-right tabular-nums">
                    {fmtPct(s.coverage_pct)}
                  </td>
                  <td className="p-1.5 text-right tabular-nums">
                    {s.mean_accepted == null ? "—" : s.mean_accepted.toFixed(3)}
                  </td>
                  <td className="p-1.5 text-right tabular-nums">
                    {s.mean_rejected == null ? "—" : s.mean_rejected.toFixed(3)}
                  </td>
                  <td className="p-1.5">
                    <InlineBar
                      frac={Math.max(
                        (s.mean_accepted ?? 0) - (s.mean_rejected ?? 0),
                        0,
                      )}
                      color={DECISION_COLOR.accept}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Review taxonomy"
          meta="why pairs are borderline"
          glossaryTerm="review_taxonomy"
        >
          {Object.entries(taxAgg)
            .sort((a, b) => b[1].total - a[1].total)
            .map(([type, v]) => (
              <div
                key={type}
                className="flex items-center justify-between py-1 text-sm text-[#2B4752]"
              >
                <span className="text-xs">{type}</span>
                <span>
                  <span className="font-semibold tabular-nums">
                    {fmtNum(v.total)}
                  </span>{" "}
                  · merge rate{" "}
                  <span className="font-semibold tabular-nums">
                    {v.total > 0
                      ? `${((100 * v.merged) / v.total).toFixed(0)}%`
                      : "—"}
                  </span>
                </span>
              </div>
            ))}
          {Object.keys(taxAgg).length === 0 && (
            <p className="text-sm text-[#2B4752]">
              No reviewed pairs in this confidence band.
            </p>
          )}
        </SectionCard>

        <SectionCard
          title="Judge mix"
          meta="who decided, by decision label"
          glossaryTerm="judge_mix"
        >
          {Object.entries(judgeAgg).map(([actor, mix]) => (
            <div key={actor} className="py-1">
              <div className="text-xs font-semibold text-[#2B4752]">
                {actor}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {Object.entries(mix).map(([st, n]) => (
                  <span key={st} className="inline-flex items-center gap-1">
                    <DecisionPill status={st} />
                    <span className="text-sm font-semibold tabular-nums text-[#2B4752]">
                      {fmtNum(n)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(judgeAgg).length === 0 && (
            <p className="text-sm text-[#2B4752]">
              No judge decisions in this confidence band.
            </p>
          )}
        </SectionCard>
      </div>

      <SectionCard
        title="Pair explorer"
        meta="expand a row for the full 15-signal breakdown"
        extra={
          <div className="flex items-center gap-2">
            <Input.Search
              allowClear
              placeholder="Search site names"
              style={{ width: 220 }}
              onSearch={(v) => {
                setQ(v);
                setPage(1);
              }}
            />
            <Select
              value={status}
              style={{ width: 160 }}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { value: "", label: "All decisions" },
                ...Object.entries(STATUS_META).map(([v, m]) => ({
                  value: v,
                  label: m.label,
                })),
              ]}
            />
            <Select
              value={basket}
              style={{ width: 170 }}
              onChange={(v) => {
                setBasket(v);
                setPage(1);
              }}
              options={[
                { value: "", label: "All baskets" },
                ...dist.baskets.map((b) => ({
                  value: b.basket,
                  label: b.basket,
                })),
              ]}
            />
          </div>
        }
      >
        <Table<PairRow>
          size="small"
          rowKey="id"
          loading={pairsLoading}
          dataSource={pairs?.results ?? []}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: pairs?.count ?? 0,
            showSizeChanger: false,
            showTotal: (total) => `${fmtNum(total)} pairs`,
            onChange: setPage,
          }}
          scroll={{ y: 480 }}
          expandable={{
            expandedRowRender: (r) => <SignalBreakdown row={r} />,
          }}
          columns={[
            {
              title: "Site A",
              dataIndex: "name_a",
              ellipsis: true,
              render: (v: string | null, r) => (
                <span>
                  {dash(v)}
                  <Button
                    type="link"
                    size="small"
                    className="px-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDrill(r.norm_site_id_a);
                    }}
                  >
                    drill
                  </Button>
                </span>
              ),
            },
            {
              title: "City A",
              dataIndex: "city_a",
              width: 120,
              ellipsis: true,
              render: (v: string | null) => dash(v),
            },
            {
              title: "Site B",
              dataIndex: "name_b",
              ellipsis: true,
              render: (v: string | null, r) => (
                <span>
                  {dash(v)}
                  <Button
                    type="link"
                    size="small"
                    className="px-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDrill(r.norm_site_id_b);
                    }}
                  >
                    drill
                  </Button>
                </span>
              ),
            },
            {
              title: "City B",
              dataIndex: "city_b",
              width: 120,
              ellipsis: true,
              render: (v: string | null) => dash(v),
            },
            {
              title: "Confidence",
              dataIndex: "confidence",
              width: 100,
              render: (v: number | null) => (
                <span className="tabular-nums">{fmtConf(v)}</span>
              ),
            },
            {
              title: "Weighted",
              dataIndex: "weighted_score",
              width: 90,
              render: (v: number | null) => (
                <span className="tabular-nums">
                  {v == null ? "—" : v.toFixed(2)}
                </span>
              ),
            },
            {
              title: "Basket",
              dataIndex: "accept_basket",
              width: 130,
              render: (v: string | null) => (v ? <Chip>{v}</Chip> : "—"),
            },
            {
              title: "Decision",
              dataIndex: "match_status",
              width: 130,
              render: (v: string) => <DecisionPill status={v} />,
            },
          ]}
        />
      </SectionCard>

      <Drawer
        open={!!drill}
        onClose={() => setDrill(null)}
        width={680}
        title="Site drilldown — all scored pairs"
      >
        {drill && <SiteDrilldown nsid={drill} />}
      </Drawer>
    </div>
  );
}
